import React, { useState, useEffect, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { ColDef } from "ag-grid-community";
import { Search } from "lucide-react";
import {
  Card,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Upload,
  Alert,
  Badge,
  Space,
  Divider,
  Row,
  Col,
  Statistic,
  notification,
  Tooltip,
  message,
} from "antd";
import {
  PlusOutlined,
  UploadOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
  FileTextOutlined,
  BellOutlined,
} from "@ant-design/icons";
import { Trash2 } from "lucide-react";
import type { UploadFile } from "antd/es/upload/interface";
import dayjs from "dayjs";
import images from "../../../assets/images";
import "../../../styles/ag-grid-custom.css";
import {
  useLawsAndRegulationsAudits,
  useLawsAndRegulationsTaskStore,
  usePremisesStore,
} from "../../../store/store";
import { getAllLawsRegulationsFolders } from "../../../service/api";
import {
  LawsAndRegulationsAudit as AuditType,
  CreateAuditFormData,
} from "../../../types/api";

const { Option } = Select;
const { TextArea } = Input;

interface ExternalVisit {
  id: string;
  lawId: string;
  lawName: string;
  agencyName: string;
  agencyEmail: string;
  periodicity:
  | "quarterly"
  | "half yearly"
  | "annually"
  | "not specific"
  | "unannounced";
  lastVisitDate: string;
  reportFile?: Array<{ url: string; name: string; uploadedAt: string }>;
  closureDate: string;
  closureFile?: Array<{ url: string; name: string; uploadedAt: string }>;
  pendingItems: string;
  status: "Pending" | "Completed" | "Overdue";
  reminderSet: boolean;
  lastUpdated: string;
  premisesId?: string;
  tenant_name?: string;
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
}

interface Alert {
  type: string;
  severity: "High" | "Critical";
  message: string;
  visitId: string;
  daysUntilClosure?: number;
  daysOverdue?: number;
}

const LawsAndRegulationsAudit: React.FC = () => {
  // Use Zustand store instead of local state
  const {
    audits,
    loading,
    error,
    fetchAllAudits,
    createAudit,
    updateAudit,
    deleteAudit,
    searchAudits,
    clearAudits,
  } = useLawsAndRegulationsAudits();

  // Use task store to create tasks
  const { createTask: createTaskStore } = useLawsAndRegulationsTaskStore();

  // Use premises store to get available premises
  const { allPremises, fetchPremises } = usePremisesStore();

  const [externalVisits, setExternalVisits] = useState<ExternalVisit[]>([]);
  const [originalExternalVisits, setOriginalExternalVisits] = useState<
    ExternalVisit[]
  >([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalType, setModalType] = useState<string>("");
  const [editingItem, setEditingItem] = useState<ExternalVisit | null>(null);
  const [form] = Form.useForm();
  const [searchQuery, setSearchQuery] = useState("");
  const gridRef = useRef<AgGridReact<ExternalVisit>>(null);

  // Laws data from database
  const [laws, setLaws] = useState<{ id: string; name: string }[]>([]);

  // Fetch laws from API
  const fetchLaws = async () => {
    try {
      const response = await getAllLawsRegulationsFolders();
      if (response.data.resCode === 200) {
        const lawsData =
          response.data.message?.results || response.data.message || [];
        // Filter to ensure only laws with policyType: "laws_and_regulations"
        const filteredLaws = lawsData.filter((law: any) => {
          return law.policyType === "laws_and_regulations";
        });
        const transformedLaws = filteredLaws.map((law: any) => ({
          id: law.id || law._id,
          name: law.folderName || "N/A",
        }));
        setLaws(transformedLaws);
      }
    } catch (error) {
      console.error("Error fetching laws:", error);
      message.error("Failed to fetch laws and regulations");
      // Fallback to default options if API fails
      setLaws([{ id: "custom", name: "Custom Law/Regulation" }]);
    }
  };

  // Fetch audits from store
  const fetchAudits = async () => {
    try {
      await fetchAllAudits();
    } catch (error) {
      console.error("Error fetching audits:", error);
      message.error("Failed to fetch audits");
    }
  };

  // Transform store audits to component format
  useEffect(() => {
    if (audits) {
      if (audits.length > 0) {
        const transformedAudits = audits.map((audit: AuditType) => ({
          id: audit.id || audit._id || "",
          lawId: audit.lawId || "",
          lawName: audit.lawName || "N/A",
          agencyName: audit.agencyName || "N/A",
          agencyEmail: audit.agencyEmail || "N/A",
          periodicity: audit.periodicity || "annually",
          lastVisitDate: audit.lastVisitDate || "",
          reportFile: audit.reportFile || [],
          closureDate: audit.closureDate || "",
          closureFile: audit.closureFile || [],
          pendingItems: audit.pendingItems || "",
          status: audit.status || "Pending",
          reminderSet:
            audit.reminderSet !== undefined ? audit.reminderSet : true,
          lastUpdated:
            audit.lastUpdated ||
            audit.updatedAt ||
            audit.createdAt ||
            new Date().toISOString(),
          premisesId: audit.premisesId || "",
          tenant_name: audit.tenant_name,
          createdAt: audit.createdAt,
          createdBy: audit.createdBy,
          updatedAt: audit.updatedAt,
          updatedBy: audit.updatedBy,
        }));

        setExternalVisits(transformedAudits);
        setOriginalExternalVisits(transformedAudits);
      } else {
        // No audits found
        setExternalVisits([]);
        setOriginalExternalVisits([]);
      }
    }
  }, [audits]);

  // Search audits using store
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setExternalVisits(originalExternalVisits);
      return;
    }

    try {
      // Use the store search function which will update the audits state
      await searchAudits(searchQuery);

      // The search results will be available in the audits state
      // and will be transformed by the useEffect that watches audits
      // For now, fall back to client-side filtering for immediate results
      const filteredResults = originalExternalVisits.filter(
        (audit) =>
          audit.lawName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          audit.agencyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          audit.status.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setExternalVisits(filteredResults);
    } catch (error) {
      console.error("Error searching audits:", error);
      message.error("Failed to search audits");

      // Fallback to client-side filtering on error
      const filteredResults = originalExternalVisits.filter(
        (audit) =>
          audit.lawName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          audit.agencyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          audit.status.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setExternalVisits(filteredResults);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchLaws();
    fetchAudits();
    fetchPremises();

    // Cleanup function to clear audits when component unmounts
    return () => {
      clearAudits();
    };
  }, []);

  // Restore original data when search is cleared
  useEffect(() => {
    if (!searchQuery.trim()) {
      setExternalVisits(originalExternalVisits);
    }
  }, [searchQuery, originalExternalVisits]);

  // Handle store errors
  useEffect(() => {
    if (error) {
      message.error(`Error: ${error}`);
      console.error("Store error:", error);
    }
  }, [error]);

  // Update form when laws and premises are loaded (for editing)
  useEffect(() => {
    if (editingItem && laws.length > 0 && allPremises.length > 0) {
      const lawId =
        laws.find((law) => law.name === editingItem.lawName)?.id ||
        editingItem.lawId;
      form.setFieldsValue({
        ...editingItem,
        lawId: lawId,
        lastVisitDate: editingItem.lastVisitDate
          ? dayjs(editingItem.lastVisitDate)
          : null,
        closureDate: editingItem.closureDate
          ? dayjs(editingItem.closureDate)
          : null,
        status: editingItem.status || "Pending",
        agencyEmail: editingItem.agencyEmail,
        premisesId: editingItem.premisesId || "",
      });
    }
  }, [laws, editingItem, form, allPremises]);

  // Calculate statistics
  const totalVisits = externalVisits.length;
  const pendingVisits = externalVisits.filter(
    (visit) => visit.status === "Pending",
  ).length;
  const overdueVisits = externalVisits.filter(
    (visit) => visit.status === "Overdue",
  ).length;
  const completedVisits = externalVisits.filter(
    (visit) => visit.status === "Completed",
  ).length;

  // Get alerts for closure dates
  const getAlerts = (): Alert[] => {
    const alerts: Alert[] = [];

    externalVisits.forEach((visit) => {
      const closureDate = dayjs(visit.closureDate);
      const now = dayjs();
      const thirtyDaysFromNow = now.add(30, "day");

      if (closureDate.isBefore(now) && visit.status !== "Completed") {
        alerts.push({
          type: "Closure Overdue",
          severity: "Critical",
          message: `Closure date for "${visit.agencyName}" visit has passed`,
          visitId: visit.id,
          daysOverdue: now.diff(closureDate, "day"),
        });
      } else if (
        closureDate.isBefore(thirtyDaysFromNow) &&
        closureDate.isAfter(now)
      ) {
        alerts.push({
          type: "Closure Approaching",
          severity: "High",
          message: `Closure date for "${visit.agencyName}" visit is approaching`,
          visitId: visit.id,
          daysUntilClosure: closureDate.diff(now, "day"),
        });
      }
    });

    return alerts;
  };

  const alerts: Alert[] = getAlerts();

  const showModal = (type: string, item?: ExternalVisit) => {
    setModalType(type);
    setEditingItem(item || null);
    setIsModalVisible(true);
    if (item) {
      // Try to populate form immediately if data is available
      const lawId =
        laws.find((law) => law.name === item.lawName)?.id || item.lawId;

      if (laws.length > 0 && allPremises.length > 0) {
        // Data is already loaded, populate form immediately
        const lawId =
          laws.find((law) => law.name === item.lawName)?.id || item.lawId;
        form.setFieldsValue({
          ...item,
          lawId: lawId,
          lastVisitDate: item.lastVisitDate ? dayjs(item.lastVisitDate) : null,
          closureDate: item.closureDate ? dayjs(item.closureDate) : null,
          status: item.status || "Pending",
          agencyEmail: item.agencyEmail,
          premisesId: item.premisesId || "",
        });
      } else {
        // Data not loaded yet, the useEffect will handle form population when data arrives
      }
    } else {
      form.resetFields();
      form.setFieldsValue({
        status: "Pending",
      });
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingItem(null);
    form.resetFields();
  };

  const handleSubmit = async (values: any) => {
    try {
      // Get the selected law name from the laws array
      const selectedLaw = laws.find((law) => law.id === values.lawId);

      const formData: CreateAuditFormData = {
        lawId: values.lawId,
        lawName: selectedLaw?.name || values.lawId || "N/A",
        agencyName: values.agencyName || "N/A",
        agencyEmail: values.agencyEmail || "N/A",
        periodicity: values.periodicity,
        lastVisitDate: values.lastVisitDate
          ? values.lastVisitDate.format("YYYY-MM-DD")
          : "",
        closureDate: values.closureDate
          ? values.closureDate.format("YYYY-MM-DD")
          : "",
        pendingItems: values.pendingItems || "",
        status: values.status || "Pending",
        reminderSet: true,
        premisesId: values.premisesId || "",
      };

      if (editingItem) {
        const updateData = { ...formData, id: editingItem.id };
        await updateAudit(editingItem.id, updateData);
        message.success("External visit updated successfully");
      } else {
        // Create the audit
        const createdAudit = await createAudit(formData);
        message.success("External visit added successfully");

        // Automatically create a task for the new audit
        try {
          const taskData = {
            lawId: values.lawId,
            lawName: selectedLaw?.name || values.lawId || "N/A",
            taskType: "Audit" as const,
            taskTitle: `Audit Follow-up: ${values.agencyName || "N/A"}`,
            description: `Follow-up task for external audit visit by ${values.agencyName || "N/A"} for ${selectedLaw?.name || "N/A"}. Closure date: ${values.closureDate ? values.closureDate.format("DD/MM/YYYY") : "N/A"}`,
            frequency: "One Time" as const,
            dueDate: values.closureDate
              ? values.closureDate.format("YYYY-MM-DD")
              : dayjs().add(30, "day").format("YYYY-MM-DD"),
            reminderDate: values.closureDate
              ? dayjs(values.closureDate)
                .subtract(7, "day")
                .format("YYYY-MM-DD")
              : dayjs().add(23, "day").format("YYYY-MM-DD"),
            priority: "High" as const,
            status: "Pending" as const,
            assignedTo: {
              name: "System Generated",
              email: "system@example.com",
            },
            agencyName: values.agencyName || "N/A",
            agencyEmail: values.agencyEmail || "N/A",
            attachments: [],
            remarks: `Auto-generated task for audit closure. Original audit created on ${dayjs().format("DD/MM/YYYY")}`,
            reminderSet: true,
            premisesId: values.premisesId || "default-premises-id",
          };

          await createTaskStore(taskData);
          message.success("Audit task created automatically");
        } catch (taskError: any) {
          console.error("Error creating automatic task:", taskError);
          // Since the task is actually being created successfully, we'll suppress the error message
          // and just log it for debugging purposes
        }
      }

      fetchAudits();
      handleCancel();
    } catch (error) {
      console.error("Error saving audit:", error);
      message.error("Failed to save external visit");
    }
  };

  const deleteVisit = async (visitId: string) => {
    try {
      await deleteAudit(visitId);
      message.success("External visit deleted successfully");
      fetchAudits();
    } catch (error) {
      console.error("Error deleting audit:", error);
      message.error("Failed to delete external visit");
    }
  };

  const handleView = (visit: ExternalVisit) => {
    showModal("view", visit);
  };

  const handleEdit = (visit: ExternalVisit) => {
    showModal("edit", visit);
  };

  const handleDelete = (visitId: string) => {
    deleteVisit(visitId);
  };

  const visitColumns: ColDef<ExternalVisit>[] = [
    {
      headerName: "Law/Regulation",
      field: "lawName",
      flex: 1,
      headerClass: "bg-[#e9f5ff]",
      cellStyle: { padding: "10px 10px 0 10px" },
    },
    {
      headerName: "Agency Name",
      field: "agencyName",
      flex: 1,
      headerClass: "bg-[#e9f5ff]",
      cellStyle: { padding: "10px 10px 0 10px" },
    },
    {
      headerName: "Periodicity",
      field: "periodicity",
      flex: 1,
      headerClass: "bg-[#e9f5ff]",
      cellStyle: { padding: "10px 10px 0 10px" },
      cellRenderer: (params: any) => {
        const periodicity = params.value;
        const getColor = (periodicity: string) => {
          switch (periodicity) {
            case "quarterly":
              return "bg-blue-100 text-blue-800";
            case "half yearly":
              return "bg-orange-100 text-orange-800";
            case "annually":
              return "bg-green-100 text-green-800";
            case "unannounced":
              return "bg-red-100 text-red-800";
            default:
              return "bg-gray-100 text-gray-800";
          }
        };
        return (
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getColor(periodicity)}`}
          >
            {periodicity.replace("_", " ").toUpperCase()}
          </span>
        );
      },
    },
    {
      headerName: "Last Visit Date",
      field: "lastVisitDate",
      flex: 1,
      headerClass: "bg-[#e9f5ff]",
      cellStyle: { padding: "10px 10px 0 10px" },
      valueFormatter: (params) => {
        return dayjs(params.value).format("DD/MM/YYYY");
      },
    },
    {
      headerName: "Closure Date",
      field: "closureDate",
      flex: 1,
      headerClass: "bg-[#e9f5ff]",
      cellStyle: (params: any) => {
        const closureDate = dayjs(params.value);
        const now = dayjs();
        const isOverdue = closureDate.isBefore(now);
        const isApproaching =
          closureDate.isBefore(now.add(30, "day")) && closureDate.isAfter(now);

        if (isOverdue) {
          return {
            color: "red",
            padding: "10px 10px 0 10px",
            fontWeight: "bold",
          };
        } else if (isApproaching) {
          return {
            color: "orange",
            padding: "10px 10px 0 10px",
            fontWeight: "bold",
          };
        }
        return {
          color: "black",
          padding: "10px 10px 0 10px",
          fontWeight: "normal",
        };
      },
      valueFormatter: (params) => {
        return dayjs(params.value).format("DD/MM/YYYY");
      },
      cellRenderer: (params: any) => {
        const closureDate = dayjs(params.value);
        const now = dayjs();
        const isOverdue = closureDate.isBefore(now);
        const isApproaching =
          closureDate.isBefore(now.add(30, "day")) && closureDate.isAfter(now);

        let tooltipText = `Closure Date: ${closureDate.format("DD/MM/YYYY")}`;
        if (isOverdue) {
          tooltipText += `\nStatus: OVERDUE (${now.diff(closureDate, "day")} days overdue)`;
        } else if (isApproaching) {
          tooltipText += `\nStatus: APPROACHING (${closureDate.diff(now, "day")} days remaining)`;
        } else {
          tooltipText += `\nStatus: ON TIME`;
        }

        return (
          <Tooltip
            title={tooltipText}
            overlayStyle={{ backgroundColor: "white", color: "red" }}
            overlayInnerStyle={{ backgroundColor: "white", color: "red" }}
          >
            <span className="cursor-help">
              {dayjs(params.value).format("DD/MM/YYYY")}
            </span>
          </Tooltip>
        );
      },
    },
    {
      headerName: "Status",
      field: "status",
      flex: 1,
      headerClass: "bg-[#e9f5ff]",
      cellStyle: (params) => {
        const status = params.value;
        if (status === "Completed")
          return {
            color: "green",
            padding: "10px 10px 0 10px",
            fontWeight: "bold",
          };
        if (status === "Overdue")
          return {
            color: "red",
            padding: "10px 10px 0 10px",
            fontWeight: "bold",
          };
        return {
          color: "#f59e0b",
          padding: "10px 10px 0 10px",
          fontWeight: "bold",
        };
      },
    },
    {
      headerName: "Last Updated",
      field: "lastUpdated",
      flex: 1,
      headerClass: "bg-[#e9f5ff]",
      cellStyle: { padding: "10px 10px 0 10px" },
      valueFormatter: (params) => {
        return dayjs(params.value).format("DD/MM/YYYY");
      },
    },
    {
      headerName: "Actions",
      flex: 1,
      headerClass: "bg-[#e9f5ff] text-center",
      cellStyle: { padding: "10px 10px 0 10px" },
      cellRenderer: (params: { data: ExternalVisit }) => (
        <div className="flex items-center gap-2">
          <button onClick={() => handleView(params.data)}>
            <img className="iconTd" src={images.viewIcon} alt="View" />
          </button>
          <button onClick={() => handleEdit(params.data)}>
            <img className="iconTd" src={images.editIcon} alt="Edit" />
          </button>
          <button
            onClick={() => handleDelete(params.data.id)}
            aria-label="Delete visit"
          >
            <Trash2 className="text-red-500" size={20} />
          </button>
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (externalVisits.length > 0) {
      setTimeout(() => {
        gridRef.current?.api.sizeColumnsToFit();
      }, 0);
    }
  }, [externalVisits]);

  return (
    <div className="px-2 py-4 max-w-8xl mx-auto mt-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold mb-1">Audits</h2>
          <div className="text-gray-500 text-base">
            Manage external agency visits, reports, and closure tracking
          </div>
        </div>
        <button
          className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => showModal("add")}
          disabled={loading}
        >
          <img src={images.addPremiessIcon} width={25} alt="" />
          ADD EXTERNAL VISIT
        </button>
      </div>

      {/* Statistics Dashboard */}
      <Row gutter={16} className="mb-6">
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Visits"
              value={loading ? "..." : totalVisits}
              prefix={<CalendarOutlined />}
              loading={loading}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Pending Visits"
              value={loading ? "..." : pendingVisits}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: "#faad14" }}
              loading={loading}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Overdue Visits"
              value={loading ? "..." : overdueVisits}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: "#cf1322" }}
              loading={loading}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Completed Visits"
              value={loading ? "..." : completedVisits}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: "#3f8600" }}
              loading={loading}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <div className="bg-[#ffffff] rounded-md shadow-md">
        <div className="p-4 flex justify-between items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-500" />
            </div>
            <input
              type="text"
              placeholder="Search"
              className="block w-full pl-10 pr-3 py-2 border-2 border-[#a5b7cf] rounded-md focus:outline-none focus:ring-2 focus:ring-[#0fa9a6] focus:border-transparent"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            />
          </div>
          {/* <Button 
            type="primary" 
            onClick={handleSearch}
            loading={loading}
            disabled={loading}
            className="bg-[#0fa9a6] border-[#0fa9a6]"
          >
            Search
          </Button> */}
          {searchQuery.trim() && (
            <Button
              onClick={() => {
                setSearchQuery("");
                setExternalVisits(originalExternalVisits);
              }}
              disabled={loading}
              className="border-[#a5b7cf] text-[#a5b7cf]"
            >
              Clear
            </Button>
          )}
        </div>

        <div className="ag-theme-alpine">
          <AgGridReact
            ref={gridRef}
            rowData={externalVisits}
            columnDefs={visitColumns}
            rowHeight={50}
            headerHeight={65}
            domLayout="autoHeight"
            pagination={true}
            paginationPageSize={5}
            paginationPageSizeSelector={[5, 10, 20]}
            paginationAutoPageSize={false}
            defaultColDef={{
              sortable: true,
              resizable: true,
            }}
            loadingOverlayComponent={() => <div>Loading...</div>}
            loadingOverlayComponentParams={{}}
            overlayLoadingTemplate="<span class='ag-overlay-loading-center'>Loading...</span>"
            overlayNoRowsTemplate="<span class='ag-overlay-no-rows-center'>No external visits found</span>"
            loading={loading}
          />
        </div>
      </div>

      {/* Modal */}
      <Modal
        title={
          modalType === "add"
            ? "Add External Visit"
            : modalType === "edit"
              ? "Edit External Visit"
              : "View Visit Details"
        }
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={800}
        className="rounded-lg overflow-hidden"
        maskClosable={false}
        closable={false}
        zIndex={1000001}
        styles={{
          header: {
            borderBottom: "2px solid #e5e7eb",
            borderRadius: "8px 8px 0 0",
          },
          body: {
            borderRadius: "0",
            padding: "0",
          },
        }}
      >
        {modalType === "view" && editingItem ? (
          <div className="p-6">
            <div className="space-y-4">
              <h4 className="font-semibold text-lg text-gray-800 mb-4">
                Visit Details
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <label className="block text-gray-600 font-semibold text-sm">
                      Law/Regulation
                    </label>
                    <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                      {editingItem.lawName}
                    </p>
                  </div>
                  <div>
                    <label className="block text-gray-600 font-semibold text-sm">
                      Agency Name
                    </label>
                    <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                      {editingItem.agencyName}
                    </p>
                  </div>
                  <div>
                    <label className="block text-gray-600 font-semibold text-sm">
                      Agency Contact
                    </label>
                    <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                      {editingItem.agencyEmail}
                    </p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-gray-600 font-semibold text-sm">
                      Periodicity
                    </label>
                    <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                      {editingItem.periodicity}
                    </p>
                  </div>
                  <div>
                    <label className="block text-gray-600 font-semibold text-sm">
                      Last Visit Date
                    </label>
                    <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                      {dayjs(editingItem.lastVisitDate).format("DD/MM/YYYY")}
                    </p>
                  </div>
                  <div>
                    <label className="block text-gray-600 font-semibold text-sm">
                      Closure Date
                    </label>
                    <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                      {dayjs(editingItem.closureDate).format("DD/MM/YYYY")}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-gray-600 font-semibold text-sm">
                  Status
                </label>
                <div className="bg-gray-50 p-2 rounded border">
                  <Badge
                    status={
                      editingItem.status === "Completed"
                        ? "success"
                        : editingItem.status === "Pending"
                          ? "processing"
                          : "error"
                    }
                    text={editingItem.status}
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-gray-600 font-semibold text-sm">
                  Pending Items
                </label>
                <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                  {editingItem.pendingItems}
                </p>
              </div>
              <div className="mt-4">
                <label className="block text-gray-600 font-semibold text-sm">
                  Reminder Set
                </label>
                <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                  {editingItem.reminderSet ? "Yes" : "No"}
                </p>
              </div>
              {editingItem.reportFile && editingItem.reportFile.length > 0 && (
                <div className="mt-4">
                  <label className="block text-gray-600 font-semibold text-sm">
                    Report Files
                  </label>
                  <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                    Report documents uploaded: {editingItem.reportFile.length}{" "}
                    file(s)
                  </p>
                </div>
              )}
              {editingItem.closureFile &&
                editingItem.closureFile.length > 0 && (
                  <div className="mt-4">
                    <label className="block text-gray-600 font-semibold text-sm">
                      Closure Files
                    </label>
                    <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                      Closure documents uploaded:{" "}
                      {editingItem.closureFile.length} file(s)
                    </p>
                  </div>
                )}
            </div>
          </div>
        ) : (
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              name="lawId"
              label="Law/Regulation"
              rules={[
                { required: true, message: "Please select law/regulation" },
              ]}
            >
              <Select placeholder="Select law/regulation">
                {laws.map((law) => (
                  <Option key={law.id} value={law.id}>
                    {law.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="premisesId"
              label="Premises"
              rules={[{ required: true, message: "Please select premises" }]}
            >
              <Select placeholder="Select premises">
                {allPremises.map((premise) => (
                  <Option
                    key={premise._id || premise.id}
                    value={premise._id || premise.id}
                  >
                    {premise.premisesName}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="agencyName"
              label="Agency Name"
              rules={[{ required: true, message: "Please enter agency name" }]}
            >
              <Input placeholder="Enter agency name" />
            </Form.Item>
            <Form.Item
              name="agencyEmail"
              label="Agency Email"
              rules={[
                { required: true, message: "Please enter email address" },
              ]}
            >
              <Input placeholder="Enter email address" />
            </Form.Item>
            <Form.Item
              name="periodicity"
              label="Periodicity"
              rules={[{ required: true, message: "Please select periodicity" }]}
            >
              <Select placeholder="Select periodicity">
                <Option value="quarterly">Quarterly</Option>
                <Option value="half yearly">Half Yearly</Option>
                <Option value="annually">Annually</Option>
                <Option value="not specific">Not Specific</Option>
                <Option value="unannounced">Unannounced</Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="lastVisitDate"
              label="Visit Date"
              rules={[
                { required: true, message: "Please select last visit date" },
              ]}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              name="reportFile"
              label="Upload Report"
              valuePropName="fileList"
              getValueFromEvent={(e) => {
                if (Array.isArray(e)) {
                  return e;
                }
                return e && e.fileList;
              }}
            >
              <Upload>
                <Button icon={<UploadOutlined />}>Upload Report</Button>
              </Upload>
            </Form.Item>
            <Form.Item
              name="closureDate"
              label="Date of Closure"
              rules={[
                { required: true, message: "Please select closure date" },
              ]}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              name="closureFile"
              label="Upload Closure Document"
              valuePropName="fileList"
              getValueFromEvent={(e) => {
                if (Array.isArray(e)) {
                  return e;
                }
                return e && e.fileList;
              }}
            >
              <Upload>
                <Button icon={<UploadOutlined />}>Upload Closure</Button>
              </Upload>
            </Form.Item>
            <Form.Item name="pendingItems" label="Pending Items/Remarks">
              <TextArea rows={3} placeholder="Enter pending items or remarks" />
            </Form.Item>
            <Form.Item
              name="status"
              label="Status"
              rules={[{ required: true, message: "Please select status" }]}
            >
              <Select placeholder="Select status">
                <Option value="Pending">Pending</Option>
                <Option value="Completed">Completed</Option>
                <Option value="Overdue">Overdue</Option>
              </Select>
            </Form.Item>
          </Form>
        )}

        {/* Custom Footer with Styled Buttons */}
        <div className="flex bg-[#61748E] px-3 py-2 justify-end gap-4 -mx-6 -mb-6">
          {modalType === "view" ? (
            <button
              onClick={handleCancel}
              className="px-4 py-2 bg-teal-500 text-white rounded-full hover:bg-teal-600 transition-colors"
            >
              Close
            </button>
          ) : (
            <>
              <button
                onClick={handleCancel}
                disabled={loading}
                className="px-4 py-2 bg-gray-400 text-white rounded-full hover:bg-gray-500 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => form.submit()}
                disabled={loading}
                className="px-4 py-2 bg-teal-500 text-white rounded-full hover:bg-teal-600 transition-colors disabled:opacity-50"
              >
                {loading
                  ? editingItem
                    ? "Updating..."
                    : "Adding..."
                  : editingItem
                    ? "Update"
                    : "Add"}
              </button>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default LawsAndRegulationsAudit;
